import React, { useCallback, useEffect, useState, useRef } from "react";
import { UseAdContext } from "../Contexts/AdContext";
import { UseUserContext } from "../Contexts/UserContext";
import Median from "median-js-bridge";
import ReactGA from "react-ga4";
import ArmanetAd from "./ArmanetAd";

function Ad(props) {
  const { GetAds, GetScoutAd } = UseAdContext();
  const { user, loggingIn } = UseUserContext();
  const [ad, SetAd] = useState([]);
  const [totalAdLoads, SetTotalAdLoads] = useState(0);
  const [inView, SetInView] = useState(false);
  // True while the latest load for this slot came back with no in-house ad, so
  // the slot is showing Armanet (see LoadAd).
  const [backfill, SetBackfill] = useState(false);
  // Counts this slot's empty loads. Each one remounts the Armanet unit (it is
  // the unit's `key`), because armanet-pxl.js asks for one ad per element and
  // never again: an unchanged element shows its first ad for every later load.
  const [backfillLoads, SetBackfillLoads] = useState(0);
  // The slot's root element swaps between the in-house <a> and the backfill
  // <div>, so the visibility observer keys on the element itself rather than on
  // adRef, whose identity never changes.
  const [slotElement, SetSlotElement] = useState(null);

  const adRef = useRef(null);
  const impressionFiredRef = useRef(false);
  // Whether the visibility observer last saw the slot on screen (see the
  // observer below).
  const inViewRef = useRef(false);

  const SlotRef = useCallback((element) => {
    adRef.current = element;
    SetSlotElement(element);
  }, []);

  const adTypes = {
    banner: 2,
    box: 3,
  };

  // Legacy ad_type_id -> Scout placement. Vendor-1 ads are re-served from Scout
  // using the matching placement slug.
  const scoutPlacements = {
    2: "banner",
    3: "box",
  };

  // Legacy vendors whose inventory Scout re-serves.
  const scoutVendorIds = [1, 46, 35, 85, 78, 14, 42];

  // Armanet unit that fills each slot type when there is no in-house ad.
  const backfillUnits = {
    banner: "backfillBanner",
    box: "backfillBox",
  };

  async function LoadAd(force = false) {
    if (!AdLoadEnabled(force)) {
      return;
    }
    SetAd([]);
    let newAd = await GetAds({
      amount: props.amount,
      typeId: adTypes[props.type],
    });
    let legacyAd = newAd?.data instanceof Array ? newAd.data[0] : null;

    // No in-house ad for this load: the draw landed on a vendor that has
    // reached its daily impression cap, nothing is booked, or the Ad Function
    // failed. Show Armanet instead of leaving the slot blank. Scout is not
    // asked: there is no vendor to ask it about.
    if (legacyAd == null) {
      ShowBackfill();
      return;
    }

    // Vendors in scoutVendorIds are served from Scout. The legacy Ad Function has
    // already logged its own (fetch-time) impression for this ad; on a
    // successful Scout serve we render the Scout ad instead and let Scout own
    // impression/click tracking. On holdout / no fill / error we fall back to
    // the legacy ad. If Scout's own cap for the vendor is reached we backfill
    // instead, as for a legacy cap: the legacy ad is the same capped vendor.
    // The legacy impression logged above stays counted, so the legacy cap
    // trips slightly early for that vendor, never late.
    if (scoutVendorIds.includes(legacyAd?.vendorId)) {
      const { ad: scoutAd, capped } = await GetScoutAd({
        placement: scoutPlacements[adTypes[props.type]],
        vendorId: legacyAd.vendorId,
      });
      if (capped) {
        ShowBackfill();
        return;
      }
      if (scoutAd) {
        SetBackfill(false);
        SetAd({
          isScout: true,
          adName: scoutAd.name,
          imagePath: scoutAd.image_url,
          fullLinkUrl: scoutAd.destination_url,
          clickUrl: scoutAd.click_url,
          impressionUrl: scoutAd.impression_url,
        });
        SetTotalAdLoads(totalAdLoads + 1);
        return;
      }
    }

    SetBackfill(false);
    SetAd(legacyAd);
    SetTotalAdLoads(totalAdLoads + 1);
  }

  // Hand this load to Armanet. Decided per load, not for the rest of the page:
  // the next refresh draws in-house again. Only the capped vendor's share of
  // loads should go to Armanet, and a slot that stayed on Armanet after one
  // unlucky draw would hand it every later refresh too. A backfilled load
  // counts toward the slot's refresh limit like any other, and asks Armanet for
  // a new ad.
  function ShowBackfill() {
    SetBackfill(true);
    SetBackfillLoads((prev) => prev + 1);
    SetTotalAdLoads(totalAdLoads + 1);
  }

  function HandleClick(e, linkUrl) {
    e.preventDefault();
    // Scout ads open their click URL, not the destination. Scout's click
    // endpoint records the click and then redirects to the destination with
    // scout_cid / scout_vid appended, which is how the vendor's pixel ties the
    // landing visit to this click. Pinging the click URL as an image and
    // opening the destination directly recorded the click but dropped both
    // ids, so vendors' click-attributed visits went to zero (FUN-125).
    const target = ad?.isScout && ad?.clickUrl ? ad.clickUrl : linkUrl;
    if (target == null) {
      return;
    }
    if (Median.isNativeApp()) {
      Median.window.open(target, "external");
      return;
    }
    window.open(target, "_blank");
  }

  function AdLoadEnabled(force = false) {
    return (
      !loggingIn &&
      !user?.isPremium &&
      adRef.current != null &&
      document.fullscreenElement == null &&
      ((inView && totalAdLoads <= 5) || force)
    );
  }

  useEffect(() => {
    let interval = setInterval(() => {
      if (AdLoadEnabled()) {
        LoadAd(props.belowCommentsForce ?? false);
      }
    }, 60000);

    return () => clearInterval(interval);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAdLoads]);

  useEffect(() => {
    if (totalAdLoads > 0) {
      return;
    }

    SetAd({});
    LoadAd(props.belowCommentsForce ?? false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggingIn, inView]);

  useEffect(() => {
    SetTotalAdLoads(0);
    LoadAd(props.belowCommentsForce ?? false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  useEffect(() => {
    if (slotElement == null) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          SetInView(entry.isIntersecting);
          // Load only when the slot comes into view. The observer re-attaches
          // every time the slot's root swaps between the in-house <a> and the
          // backfill <div>, and its first callback reports the slot as already
          // visible. Loading on that drew in-house again a moment after every
          // backfill, which handed the capped vendor's share to the other
          // vendors and unmounted the Armanet unit before it asked for an ad.
          if (entry.isIntersecting && !inViewRef.current) {
            LoadAd();
          }
          inViewRef.current = entry.isIntersecting;
        });
      },
      { threshold: 0.1 },
    );

    observer.observe(slotElement);

    return () => observer.disconnect();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotElement]);

  useEffect(() => {
    if (adRef.current != null && totalAdLoads > 0) {
      LoadAd(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.type]);

  // IAB-style viewable impression for Scout ads: fire the impression pixel only
  // after the ad has been >=50% visible for >=1 continuous second. Latched per
  // load. Legacy (non-Scout) ads keep their fetch-time server-side impression.
  useEffect(() => {
    if (!ad?.isScout || !ad?.impressionUrl) {
      return;
    }
    impressionFiredRef.current = false;
    const el = adRef.current;
    if (el == null) {
      return;
    }

    let timeoutId = null;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (timeoutId === null) {
            timeoutId = setTimeout(() => {
              if (impressionFiredRef.current) {
                return;
              }
              impressionFiredRef.current = true;
              const img = new window.Image();
              img.src = ad.impressionUrl;
              observer.disconnect();
              timeoutId = null;
            }, 1000);
          }
        } else if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ad]);

  useEffect(() => {
    if (
      ad == null ||
      ad?.vendor?.googleAnalyticsTag == null ||
      ad?.vendor?.googleAnalyticsTag === ""
    ) {
      return;
    }
    ReactGA.gtag("event", "page_view", {
      event_category: "Ad View",
      event_label: ad.adName,
      send_to: ad.vendor.googleAnalyticsTag,
    });
  }, [ad]);

  // Rendered beside, never inside, the in-house <a>: HandleClick calls
  // preventDefault and would swallow Armanet's own clicks. The slot keeps its
  // `ad <type>` classes so page layouts place it exactly where the in-house ad
  // would have been. The slot's own refresh drives the unit: each empty load
  // mounts a fresh one, so the unit does not refresh itself on top of that.
  if (backfill) {
    return (
      <div className={"ad " + props.type + " adBackfill"} ref={SlotRef}>
        <ArmanetAd
          key={backfillLoads}
          type={backfillUnits[props.type]}
          autoRefresh={false}
        />
      </div>
    );
  }

  return (
    <a
      href={ad?.fullLinkUrl}
      className={"ad " + props.type}
      onClick={(e) => {
        HandleClick(e, ad?.fullLinkUrl);
      }}
      ref={SlotRef}
    >
      {ad?.imagePath != null && !user?.isPremium && (
        <img
          src={ad.imagePath}
          alt={"Advertisement " + props.type}
          loading="lazy"
        />
      )}
    </a>
  );
}

export default Ad;
